import { subMinutes } from "date-fns";
import { transformPredictionsToChartData } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/transformPredictionsToChartData";
import { getStretchCoverageTitle } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/MeasureProgress/common";
import {
  ADDITIONAL_FROM_TIME_MINUTES,
  CAPACITY_PER_BUS,
} from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/constants";
import {
  DecisionSupportContextType,
  useDecisionSupportContextValues,
} from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/DecisionSupportContext";
import { useAlternativeTransportMeasureProgress } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/useAlternativeTransportMeasureProgress";
import { useAlternativeTransportPlanningPassengerPredictions } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/useAlternativeTransportPassengerPredictions";
import { useRelatedInfrastructureIncidentPlan } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/useRelatedInfrastructureIncidentPlan";
import { useInfrastructureEvent } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/hooks/useInfrastructureEvent";
import { useMemo } from "react";

export const useDecisionSupportData = (uuid: string | undefined) => {
  const {
    data: infrastructureInformationData,
    status: infrastructureInformationStatus,
  } = useInfrastructureEvent(uuid ? [uuid] : [], {
    messageContext: "DROPS_LOG",
  });

  const infrastructureEvent = infrastructureInformationData?.[0];

  const {
    data: relatedInfrastructureIncidentPlan,
    status: incidentPlanLoadingState,
  } = useRelatedInfrastructureIncidentPlan(infrastructureEvent?.incidentId);

  const {
    showAllStretches,
    setShowAllStretches,
    timeHorizonHours,
    setTimeHorizonHours,
    horizonFromTime,
    horizonToTime,
    totalAffected,
    setTotalAffected,
    affectedWithWheelchair,
    setAffectedWithWheelchair,
    isAffectedPending,
    setIsAffectedPending,
    isAffectedError,
    setIsAffectedError,
  } = useDecisionSupportContextValues();

  const passengerPredictions =
    useAlternativeTransportPlanningPassengerPredictions(
      infrastructureEvent?.incidentId,
      subMinutes(horizonFromTime, ADDITIONAL_FROM_TIME_MINUTES),
      horizonToTime,
    );

  const { data: measureProgressData } = useAlternativeTransportMeasureProgress(
    infrastructureEvent?.incidentId,
  );

  const incidentStretchName = useMemo(() => {
    const incidentStretchCoverage =
      measureProgressData?.incidentStretchCoverage;
    return incidentStretchCoverage
      ? getStretchCoverageTitle(incidentStretchCoverage)
      : undefined;
  }, [measureProgressData]);

  const transportNeedData = useMemo(() => {
    if (
      passengerPredictions.status === "success" &&
      passengerPredictions.data?.data
    ) {
      return transformPredictionsToChartData(
        passengerPredictions.data.data,
        incidentStretchName,
      );
    }
    return [];
  }, [
    passengerPredictions.status,
    passengerPredictions.data,
    incidentStretchName,
  ]);

  const transportNeedMaxYValue = useMemo(() => {
    const sumsByDateMs = transportNeedData.reduce<Record<string, number>>(
      (acc, d) => {
        const key = String(d.date.getTime());
        acc[key] = (acc[key] ?? 0) + (d.passengers ?? 0);
        return acc;
      },
      {},
    );
    return Math.max(0, ...Object.values(sumsByDateMs));
  }, [transportNeedData]);

  const busStatistics = useMemo(
    () => ({
      needed: Math.ceil(transportNeedMaxYValue / CAPACITY_PER_BUS),
      ordered: 0,
      confirmed: 0,
      arrived: 0,
    }),
    [transportNeedMaxYValue],
  );

  const isTransportNeedPending = passengerPredictions.status === "pending";
  const isTransportNeedError = passengerPredictions.status === "error";

  const contextValue = useMemo<DecisionSupportContextType>(
    () => ({
      timeHorizonHours,
      horizonFromTime,
      horizonToTime,
      transportNeedData,
      transportNeedMaxYValue,
      totalAffected,
      setTotalAffected,
      isTransportNeedPending,
      isTransportNeedError,
      refetchTransportNeed: () => {
        void passengerPredictions.refetch();
      },
      isAffectedPending,
      setIsAffectedPending,
      isAffectedError,
      setIsAffectedError,
      busStatistics,
      setTimeHorizonHours,
      affectedWithWheelchair,
      setAffectedWithWheelchair,
    }),
    [
      timeHorizonHours,
      horizonFromTime,
      horizonToTime,
      transportNeedData,
      transportNeedMaxYValue,
      totalAffected,
      isTransportNeedPending,
      isTransportNeedError,
      passengerPredictions.refetch,
      isAffectedPending,
      isAffectedError,
      busStatistics,
      affectedWithWheelchair,
    ],
  );

  return {
    contextValue,
    infrastructureEvent,
    infrastructureInformationStatus,
    incidentPlanLoadingState,
    relatedInfrastructureIncidentPlan,
    setShowAllStretches,
    showAllStretches,
  };
};
